// /checkout/return (hosted-only, docs/09 §4). Stripe redirects here after
// Checkout (with `?session_id=...`). Billing activation is asynchronous — it
// lands when the first invoice webhook settles — so this polls
// GET /v1/billing/entitlement until `billing_state === 'active'`, then routes
// to /settings/billing with a success toast. A manual Continue link is the
// fallback. Self-managed installs redirect home (the screen does not exist).
import { useEffect, useRef } from "react";

import { Navigate, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { getBillingEntitlement, useCreditBilling } from "../lib/billing";
import { queryKeys } from "../lib/queryKeys";
import {
  EmptyState,
  InlineError,
  RouterLink,
  Spinner,
  Stack,
  Text,
  useToast,
} from "../ui";

// Poll cadence while activation settles, and the bounded give-up ceiling:
// activation normally lands within a webhook round-trip, so after MAX_POLL_MS
// (~60s) without `active` we stop polling and surface a "still processing"
// fallback rather than spinning forever.
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_MS = 60_000;

export function CheckoutReturn() {
  const creditBilling = useCreditBilling();
  const navigate = useNavigate();
  const toast = useToast();
  // Poll deadline: fixed at mount so the give-up bound is wall-clock, not
  // attempt-count (robust to ret/refetch jitter).
  const deadlineRef = useRef(Date.now() + MAX_POLL_MS);

  const entitlement = useQuery({
    enabled: creditBilling.enabled,
    queryKey: queryKeys.billingEntitlement,
    queryFn: getBillingEntitlement,
    // Stop once active or once the give-up deadline passes; the deadline makes
    // this always terminate.
    refetchInterval: (query) => {
      if (query.state.data?.billing_state === "active") return false;
      if (Date.now() >= deadlineRef.current) return false;
      return POLL_INTERVAL_MS;
    },
  });

  const isActive = entitlement.data?.billing_state === "active";
  // Give up once the deadline passes without activation (the poll has stopped);
  // observe query timestamps so identical pending responses still trigger a
  // render at the deadline (Query tracks only fields this component reads).
  const gaveUp =
    !isActive &&
    (entitlement.dataUpdatedAt >= deadlineRef.current ||
      entitlement.errorUpdatedAt >= deadlineRef.current);

  // Synchronize with the billing backend reaching 'active': hand off to
  // /settings/billing once the subscription settles.
  useEffect(() => {
    if (isActive) {
      toast.success("Subscription active");
      void navigate({ to: "/settings/billing" });
    }
  }, [isActive, navigate, toast]);

  if (creditBilling.isPending) {
    return (
      <Stack gap={4}>
        <Spinner label="Loading" />
      </Stack>
    );
  }
  if (creditBilling.isError) {
    return (
      <InlineError>Unable to load your account. Reload to retry.</InlineError>
    );
  }
  if (!creditBilling.enabled) {
    return <Navigate to="/" />;
  }

  return (
    <Stack gap={4}>
      {entitlement.isError ? (
        <InlineError>{entitlement.error.message}</InlineError>
      ) : isActive ? (
        <EmptyState
          action={
            <RouterLink to="/settings/billing">Continue to billing</RouterLink>
          }
          description="Redirecting you to billing..."
          title="You're all set"
        />
      ) : gaveUp ? (
        <EmptyState
          action={
            <RouterLink to="/settings/billing">Continue to billing</RouterLink>
          }
          description="Your subscription is not active yet. If you completed payment, check again shortly or contact support."
          title="Still processing"
        />
      ) : (
        <Stack gap={4}>
          <Spinner label="Finalizing your subscription" />
          <Text variant="muted">Finalizing your subscription.</Text>
        </Stack>
      )}
      <RouterLink to="/checkout">Return to checkout</RouterLink>
    </Stack>
  );
}
